(() => {
  const state = {
    header: null,
    frame: null,
    retryTimer: null,
    lastMeasurement: null,
    resizeObserver: null,
    mutationObserver: null,
    observedElements: new Set()
  };

  const readHeader = () => {
    if (state.header?.isConnected) return state.header;
    state.header = document.querySelector('[data-site-header]');
    return state.header;
  };

  const publish = (measurement) => {
    const root = document.documentElement;
    const previous = state.lastMeasurement;

    const properties = [
      ['--site-header-top', measurement.top, previous?.top],
      ['--site-header-height', measurement.height, previous?.height],
      ['--site-header-offset', measurement.bottom, previous?.bottom],
      ['--desktop-menu-offset', measurement.desktopTop, previous?.desktopTop],
      ['--mobile-menu-offset', measurement.mobileTop, previous?.mobileTop]
    ];

    properties.forEach(([property, nextValue, previousValue]) => {
      if (!previous || nextValue !== previousValue) {
        root.style.setProperty(property, `${nextValue}px`);
      }
    });

    const changed = !previous
      || previous.top !== measurement.top
      || previous.bottom !== measurement.bottom
      || previous.height !== measurement.height
      || previous.desktopTop !== measurement.desktopTop
      || previous.mobileTop !== measurement.mobileTop
      || previous.scrolled !== measurement.scrolled;

    state.lastMeasurement = measurement;

    if (changed) {
      window.dispatchEvent(new CustomEvent('faf:header-offset-change', {
        detail: measurement
      }));
    }

    return measurement;
  };

  const sync = () => {
    state.frame = null;

    const header = readHeader();
    if (!header) return state.lastMeasurement;

    const rect = header.getBoundingClientRect();

    if (rect.height < 1 || rect.width < 1) {
      window.clearTimeout(state.retryTimer);
      state.retryTimer = window.setTimeout(queue, 50);
      return state.lastMeasurement;
    }

    const viewportHeight = window.visualViewport?.height
      || window.innerHeight
      || document.documentElement.clientHeight;

    const rawTop = Math.max(0, Math.min(viewportHeight, rect.top));
    const rawBottom = Math.max(rawTop, Math.min(viewportHeight, rect.bottom));
    const top = Math.max(0, Math.round(rawTop));
    const bottom = Math.max(top, Math.round(rawBottom));
    const height = Math.max(1, Math.round(rect.height));
    const scrolled = document.body.classList.contains('body-scrolled');

    /*
     * Desktop keeps the original one-pixel overlap both before and after the
     * compact header state. Mobile sits immediately below the full header,
     * then gains the same one-pixel overlap once the compact state is active.
     */
    const desktopTop = Math.max(0, bottom - 1);
    const mobileTop = Math.max(0, bottom - (scrolled ? 1 : 0));

    return publish({
      top,
      bottom,
      height,
      scrolled,
      desktopTop,
      mobileTop
    });
  };

  const queue = () => {
    if (state.frame !== null) return;
    state.frame = window.requestAnimationFrame(sync);
  };

  const observeResize = (element) => {
    if (!element || state.observedElements.has(element) || !state.resizeObserver) return;
    state.observedElements.add(element);
    state.resizeObserver.observe(element);
  };

  const observeHeader = () => {
    const header = readHeader();
    if (!header || header.dataset.offsetObserverReady === 'true') return;

    header.dataset.offsetObserverReady = 'true';

    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(queue);
      observeResize(header);

      const sectionWrapper = header.closest('.shopify-section');
      observeResize(sectionWrapper);
      observeResize(sectionWrapper?.previousElementSibling);
    }

    state.mutationObserver = new MutationObserver(queue);
    state.mutationObserver.observe(header, {
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden']
    });

    const sectionWrapper = header.closest('.shopify-section');
    if (sectionWrapper) {
      state.mutationObserver.observe(sectionWrapper, {
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden']
      });
    }

    header.addEventListener('transitionrun', queue);
    header.addEventListener('transitionend', queue);
  };

  const init = () => {
    observeHeader();
    sync();

    /* window.addEventListener('scroll', queue, { passive: true });
    document.addEventListener('scroll', queue, { passive: true, capture: true });*/
    window.addEventListener('resize', queue, { passive: true });
    window.visualViewport?.addEventListener('resize', queue, { passive: true });
    /*window.visualViewport?.addEventListener('scroll', queue, { passive: true });*/
    window.addEventListener('pageshow', queue);
    window.addEventListener('faf:header-offset-sync', sync);
    window.addEventListener('faf:scroll-lock-change', queue);

    const bodyObserver = new MutationObserver(queue);
    bodyObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  };

  window.FAFHeaderOffset = {
    sync,
    queue,
    get current() {
      return state.lastMeasurement;
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
